import { Fragment, useState } from "react";
import SelectTestCardProps from "../../services/models/selectTestCardProps.model";
import CustomText from "../atomic/CustomText";
import FavIcon from "../../assets/svg/FavIcon";
import Button from "../organism/Button";
import { useAdminContext } from "../../services/context/TourPopupContext";
import CustomPopUp from "./CustomPopup";
import RequiredFieldTooltip from "./RequiredFieldTooltip";
// Helper function to calculate age from birth date
const calculateAge = (birthDate: string | undefined): number | null => {
  if (!birthDate) return null;

  try {
    // Parse date in MM-DD-YYYY format
    const parts = birthDate.split("-");
    if (parts.length !== 3) return null;

    const [month, day, year] = parts.map(Number);

    // Validate the parsed numbers
    if (isNaN(month) || isNaN(day) || isNaN(year)) return null;
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return null;

    const birth = new Date(year, month - 1, day);
    const today = new Date();

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    // Adjust age if birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  } catch (error) {
    console.error("Error calculating age:", error);
    return null;
  }
};

// Helper function to check if patient age is less than 12
const isUnderTwelve = (birthDate: string | undefined): boolean => {
  const age = calculateAge(birthDate);
  return age !== null && age < 12;
};
function SelectTestCard({
  isFavorite,
  handleFav,
  testName,
  testDescription,
  handleSelection,
  pillBox,
  selected,
  className,
  hideFavIcon,
  ...test
}: SelectTestCardProps) {
  const [showMore, setShowMore] = useState<boolean>(false);
  const { isTourActive } = useAdminContext();
  const [popupVisible, setPopupVisible] = useState<boolean>(false);
  const isLessThanTwelve = test?.values?.patientInfo?.birthDate && isUnderTwelve(test?.values?.patientInfo?.birthDate);
  const advanceTxDisabled = test?.id === 9 && isLessThanTwelve;

  const handleCardClick = () => {
    if (!isFavorite) {
      setPopupVisible(true);
    }
    handleFav();
  };

  const handlePopupClose = (value: boolean) => {
    setPopupVisible(value)
  };


  if (popupVisible) {
    return (
      <CustomPopUp
        hidePopUp={() => handlePopupClose(false)}
        popUpTitle={"Select Test"}
        hideClose={false}
      >
        <CustomText
          text={
            "Thanks for favoriting our test. Be sure to select a test to continue with this order"
          }
          fontWeight={"font-normal"}
        />
        <div className="flex justify-end mt-4">
          <Button
            buttonTheme="primary"
            textInformation={{
              text: `CONTINUE`,
              fontWeight: "font-semibold",
            }}
            onClick={() => handlePopupClose(false)}
          />
        </div>
      </CustomPopUp>
    );
  }
  return (
    <RequiredFieldTooltip
      tooltipText={advanceTxDisabled ? "Not for ages below 12" : ""}
      required={true}
      id={`test-card-${test.id || 'disabled'}`}
      backgroundColor={"#18B5B7"}
      className={"!max-w-[500px]"}
    >
      <button
        type="button"
        disabled={isTourActive || advanceTxDisabled}
        className={`flex flex-col justify-start rounded-2xl ${selected
          ? "bg-gradient-to-r from-[#18B5B7] to-[#084E8B] text-white"
          : "bg-white"
          } p-4 border w-[255px] shadow-md hover:border-primary mr-4 mb-2 disabled:cursor-not-allowed ${className}`}
        onClick={() => handleSelection()}
      >
        <div className="flex justify-between items-center mb-4 w-full">
          {!!pillBox?.title ? (
            <div
              className={`${selected
                ? "bg-[rgba(255,255,255,0.2)] border border-[rgba(255,255,255,0.2)] "
                : "bg-sideMenuHoverBg"
                } rounded-full px-2 py-1`}
            >
              <CustomText
                text={pillBox.title}
                fontWeight="font-normal"
                fontSize="text-xs text-left"
              />
            </div>
          ) : (
            <CustomText
              text={testName}
              fontWeight="font-medium"
              fontSize="text-sm"
            />
          )}
          {!!hideFavIcon ? null : (
            <button
              type="button"
              onClick={(e) => {
                // e.stopPropagation();
                handleCardClick()
                // handleFav()
              }}
            >
              <FavIcon
                fill={isFavorite ? `${selected ? "#FE4F4F" : "#18B5B7"}` : "#fff"}
                stroke={
                  isFavorite
                    ? `${selected ? "rgba(255,255,255,0.7)" : "transparent"}`
                    : "#647188"
                }
              />
            </button>
          )}
        </div>
        <div>
          {pillBox?.title && (
            <CustomText
              text={testName}
              fontWeight="font-medium"
              fontSize="text-sm"
              className="text-left mb-4"
            />
          )}
          <CustomText
            text={testDescription}
            fontWeight="font-normal"
            fontSize={`text-xs text-left ${showMore ? "" : "line-clamp-[3]"}`}
          />
          {!!test?.DecisionDxPRAMEDescription && (
            <Fragment>
              <CustomText
                text={test?.DecisionDxPRAMEDescription?.split(":")[0]}
                fontWeight="font-medium"
                fontSize="text-sm"
                className="text-left my-4"
              />
              <CustomText
                text={test?.DecisionDxPRAMEDescription?.split(":")[1]}
                fontWeight="font-normal"
                fontSize={`text-xs text-left ${showMore ? "" : "line-clamp-[3]"}`}
              />
            </Fragment>
          )}
          {(testDescription.length > 101 || testName === "DecisionDx-UM") && (
            <Button
              buttonTheme="transparent"
              type="button"
              textInformation={{
                text: `${showMore ? "Less" : "More"}`,
                fontWeight: "font-semibold",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowMore((prevState) => !prevState);
              }}
              className={`text-left  w-full text-xs mt-2  ${selected ? "text-white" : "text-primary"
                }`}
            />
          )}
        </div>
      </button>
    </RequiredFieldTooltip>
  );
}

export default SelectTestCard;
